import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Space, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, ExperimentOutlined } from "@ant-design/icons";
import InfoAyuda, { AyudaBloque } from "../../../Components/InfoAyuda";
import { botAsistenteApi } from "../../../services/botAsistente.api";
import type { LlamadaPruebaAsistente, PruebaAsistenteTareas } from "../../../interfaces/botAsistente.interfaces";
import {
  COLOR_ICONO_PRUEBA,
  LineaPrueba,
  NivelPrueba,
  TEXTO_BOTON_PROBAR,
  TEXTO_FORMULARIO_CON_ERRORES,
  TEXTO_PROBANDO,
  anuncioDePrueba,
  huellaDePrueba,
  lineasDePrueba,
  resumenDePrueba,
  textoDelFallo,
} from "./pruebaAsistente.utils";

const { Text } = Typography;

export interface ProbarAsistenteProps {
  /** Lo que está ESCRITO en el formulario (modelo ya recortado; `temperature: null` = no enviarla), guardado o no. */
  tareas: PruebaAsistenteTareas;
  /** El formulario tiene campos con errores: no se puede probar hasta corregirlos. */
  bloqueado: boolean;
  /** Se está guardando: tampoco se prueba en ese momento. */
  guardando?: boolean;
}

type Estado =
  | { fase: "inicial" }
  | { fase: "probando" }
  | { fase: "listo"; llamada: LlamadaPruebaAsistente; huella: string; pedidas: PruebaAsistenteTareas };

/** Icono + texto alternativo: el nivel nunca se transmite solo con el color. */
const ICONO: Record<NivelPrueba, { icono: React.ReactNode; texto: string }> = {
  ok: { icono: <CheckCircleFilled aria-hidden="true" style={{ color: COLOR_ICONO_PRUEBA.ok }} />, texto: "Bien" },
  aviso: { icono: <ExclamationCircleFilled aria-hidden="true" style={{ color: COLOR_ICONO_PRUEBA.aviso }} />, texto: "Aviso" },
  error: { icono: <CloseCircleFilled aria-hidden="true" style={{ color: COLOR_ICONO_PRUEBA.error }} />, texto: "Problema" },
};

const Linea: React.FC<{ linea: LineaPrueba }> = ({ linea }) => (
  <li style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }} data-testid={`prueba-linea-${linea.clave}`} data-nivel={linea.nivel}>
    <span role="img" aria-label={ICONO[linea.nivel].texto} style={{ flex: "0 0 auto", lineHeight: "22px" }}>
      {ICONO[linea.nivel].icono}
    </span>
    {/* minWidth: 0 + overflowWrap: un nombre de modelo de 80 caracteres sin espacios no puede ensanchar la página a 360 px. */}
    <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
      <div>{linea.texto}</div>
      {linea.ayuda && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {linea.ayuda}
        </Text>
      )}
    </div>
  </li>
);

/**
 * «Probar el asistente» (round 2026-10-31): comprueba, SIN guardar nada, lo que el administrador tiene escrito en «Modelos y límites»: que el servicio del
 * bot tenga la clave de OpenAI y pueda hablar con el servidor, y que el modelo de cada tarea exista y sirva para leer y corregir un gasto.
 *
 *   · Nunca se ejecuta sola: solo al apretar el botón.
 *   · Prueba los valores escritos aunque no estén guardados. Si después de probar se cambia cualquier modelo o temperatura, el resultado se marca como
 *     vencido (y vuelve a valer si se vuelve a los mismos valores).
 *   · La prueba puede tardar ~20 s: el botón queda en «cargando» (no se puede apretar dos veces) y un aviso para lector de pantalla anuncia el inicio y el fin.
 *   · Los fallos (sin permiso, otra prueba en curso, demasiadas pruebas, el servicio del bot caído...) se explican aquí mismo, no con un aviso que desaparece.
 */
const ProbarAsistente: React.FC<ProbarAsistenteProps> = ({ tareas, bloqueado, guardando = false }) => {
  const [estado, setEstado] = useState<Estado>({ fase: "inicial" });
  const enCurso = useRef(false);
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const huellaActual = huellaDePrueba(tareas);
  const probando = estado.fase === "probando";

  const probar = async () => {
    if (enCurso.current || bloqueado || guardando) return;
    enCurso.current = true;
    // Lo que se prueba es lo que hay escrito AHORA: si la persona sigue editando mientras espera, el resultado queda vencido.
    const pedidas = tareas;
    const huella = huellaActual;
    setEstado({ fase: "probando" });
    let llamada: LlamadaPruebaAsistente;
    try {
      llamada = await botAsistenteApi.probarAsistente(pedidas);
    } catch {
      // `probarAsistente` no lanza; si algo raro pasara, se explica como una caída de la conexión.
      llamada = { tipo: "fallo", fallo: { status: null, code: null, message: "" } };
    } finally {
      enCurso.current = false;
    }
    if (montado.current) setEstado({ fase: "listo", llamada, huella, pedidas });
  };

  const listo = estado.fase === "listo" ? estado : null;
  // Un resultado vence si cambia algo de lo que se probó. Un fallo («no se pudo hacer la prueba») no habla de ningún valor: no vence.
  const vencido = listo !== null && listo.llamada.tipo === "resultado" && listo.huella !== huellaActual;

  const cuerpoResultado = () => {
    if (listo === null) return null;
    if (listo.llamada.tipo === "fallo") {
      const f = textoDelFallo(listo.llamada.fallo);
      return (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          data-testid="prueba-fallo"
          message={f.titulo}
          description={f.detalle ? <span style={{ overflowWrap: "anywhere" }}>{f.detalle}</span> : undefined}
        />
      );
    }
    const lineas = lineasDePrueba(listo.llamada.datos, listo.pedidas);
    const resumen = resumenDePrueba(lineas);
    return (
      <div style={{ marginTop: 12, opacity: vencido ? 0.55 : 1 }} data-testid="prueba-resultado" data-vencido={vencido ? "si" : "no"}>
        <Alert
          type={resumen.nivel === "ok" ? "success" : resumen.nivel === "aviso" ? "warning" : "error"}
          showIcon
          style={{ marginBottom: 12 }}
          message={resumen.texto}
          data-testid="prueba-resumen"
        />
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }} data-testid="prueba-lineas">
          {lineas.map((l) => (
            <Linea key={l.clave} linea={l} />
          ))}
        </ul>
      </div>
    );
  };

  // Lo que anuncia el lector de pantalla: el inicio y el final de la prueba (una sola región, siempre en el árbol).
  const anuncio = probando
    ? TEXTO_PROBANDO
    : listo === null
      ? ""
      : listo.llamada.tipo === "fallo"
        ? textoDelFallo(listo.llamada.fallo).titulo
        : anuncioDePrueba(resumenDePrueba(lineasDePrueba(listo.llamada.datos, listo.pedidas)));

  return (
    <Card
      size="small"
      type="inner"
      style={{ marginBottom: 16 }}
      data-testid="probar-asistente"
      title={
        <Space size={4}>
          <span>Probar el asistente</span>
          <InfoAyuda titulo="Probar el asistente" testId="ayuda-probar-asistente">
            <AyudaBloque titulo="Qué hace">
              Comprueba, sin guardar nada, los modelos que escribiste arriba: que existan en OpenAI, que sirvan para leer y corregir un gasto
              (con un mensaje de ejemplo que no usa datos reales) y que acepten la temperatura elegida. También revisa que el servicio del bot
              tenga la clave de OpenAI y pueda hablar con el servidor.
            </AyudaBloque>
            <AyudaBloque titulo="Cuándo usarla">
              Antes de guardar un cambio de modelo, o cuando el asistente deje de entender los mensajes. Prueba lo que está escrito en el
              formulario aunque todavía no lo hayas guardado.
            </AyudaBloque>
            <AyudaBloque titulo="Qué no hace">
              No guarda nada ni cambia ningún ajuste, no manda mensajes de WhatsApp y no registra gastos. Cada prueba hace unas pocas consultas
              a OpenAI, así que tiene un límite de pruebas por minuto. Usa el tiempo máximo y el máximo de tokens que están guardados.
            </AyudaBloque>
            <AyudaBloque titulo="Cuánto tarda">
              Normalmente unos segundos; si OpenAI responde lento, puede llegar a unos 40 segundos.
            </AyudaBloque>
          </InfoAyuda>
        </Space>
      }
    >
      <Space wrap align="center" style={{ maxWidth: "100%" }}>
        <Button
          icon={<ExperimentOutlined />}
          onClick={() => void probar()}
          loading={probando}
          disabled={bloqueado || guardando}
          aria-describedby="probar-asistente-estado"
          data-testid="probar-asistente-boton"
        >
          {TEXTO_BOTON_PROBAR}
        </Button>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Revisa, sin guardar nada, los modelos escritos arriba.
        </Text>
      </Space>
      {bloqueado && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" data-testid="probar-asistente-bloqueado">
            {TEXTO_FORMULARIO_CON_ERRORES}
          </Text>
        </div>
      )}

      {/* Región viva: anuncia el inicio y el final. Siempre está en el árbol (si apareciera junto con el texto, algunos lectores no lo leen). */}
      <div id="probar-asistente-estado" role="status" aria-live="polite" aria-atomic="true" data-testid="prueba-anuncio" style={probando ? { marginTop: 8 } : { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }}>
        {anuncio}
      </div>

      {vencido && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          data-testid="prueba-vencida"
          message="Cambiaste los modelos o la temperatura después de probar."
          description="Este resultado ya no corresponde a lo que está escrito. Vuelve a probar."
        />
      )}
      {cuerpoResultado()}
    </Card>
  );
};

export default ProbarAsistente;
